import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from "sonner";
import { scrollToTop } from '../lib/utils';
import { SubcriptionApis } from '../Api/SubcriptionService';
import { LicenceApis } from "../Api/LicencemanagerService"
import { dropdownApi } from "../Api/CommonService";
import { Button } from '../components/ui/button';
import { hasText, stripLeadingSpaces, emailError, errorClass, fieldA11y, requiredName, requiredCompany } from '../lib/formValidation';
import BotTrap, { useBotTrap } from '../components/BotTrap';
import FieldError from '../components/FieldError';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Check, Lock } from 'lucide-react';
import { subscriptionPlans, COUNTRY_PHONE_RULES, PERSONAL_DOMAINS } from '../data/mockData';

// Shared field styling: gray field background, same blue as the card border on focus
const FIELD_CLASS =
  "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 " +
  "data-[placeholder]:text-slate-400 " +
  "focus:border-blue-500 focus:ring-1 focus:ring-blue-500 " +
  "focus-visible:border-blue-500 focus-visible:ring-1 focus-visible:ring-blue-500 " +
  // stop Chrome autofill from painting the field light blue
  "[&:-webkit-autofill]:[-webkit-text-fill-color:#fff] " +
  "[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#1e293b] " +
  "[&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_#1e293b]";

// Dropdown list: same gray as the fields, no yellow
const DROPDOWN_CLASS =
  "bg-slate-800 border-slate-700 text-white " +
  "[&_[role=option]]:text-white " +
  "[&_[role=option][data-highlighted]]:bg-slate-700 [&_[role=option][data-highlighted]]:text-white " +
  "[&_[role=option]:focus]:bg-slate-700 [&_[role=option]:focus]:text-white";

// Message for each required field (null = valid)
const validators = {
  companyName: (v) => requiredCompany(v, 'Company name', 50),
  firstName: (v) => requiredName(v, 'First name', 25),
  lastName: (v) => requiredName(v, 'Last name', 25),
};

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || "Standard";
  const sessionId = searchParams.get('session_id');
  const success = searchParams.get('success');
  const cancelled = searchParams.get('cancelled');
  const selectedPlan = subscriptionPlans.find(p => p.name === planId) || subscriptionPlans[1];
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [isAgreed, setIsAgreed] = useState(false);
  const trap = useBotTrap();
  const [fieldTouched, setFieldTouched] = useState({});
  // A field shows its message once the user has visited it or typed something,
  // so they always know why the button is disabled
  const getError = (field) => {
    const show = fieldTouched[field] || hasText(formData[field]);
    return show ? validators[field](formData[field]) : null;
  };
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_PHONE_RULES[8]);
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  const [errors, setErrors] = useState({
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
  });
  // const [timeZonOptions, setTimeZonOptions] = useState([]);
  // const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    companySize: null,
    industry: null,
    timeZone: null,

    // Billing Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',

    // Address
    // country: null,
    // state: null,
    // city: null,
    // address: null,
    // zipCode: null,

    // Payment Information
    // cardNumber: '',
    // expiryDate: '',
    // cvv: '',
    // cardholderName: '',

    // Plan Details
    userCount: 5,
    billingCycle: "monthly"
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('');

  // Trial-eligible plan for "Prepaid" customer type, resolved from the two APIs below
  const [trialEligiblePlan, setTrialEligiblePlan] = useState(null);
  const [loadingTrialPlan, setLoadingTrialPlan] = useState(false);

  const fetchEmployees = async () => {
    try {
      const res = await dropdownApi.fetchAllNomOfEmployee();
      setEmployeeOptions(res.data || []);
    } catch (err) {
      toast.error("Failed to load employee options");
    }
  };

  const fetchIndustries = async () => {
    try {
      const res = await dropdownApi.fetchAllIndustry();
      setIndustryOptions(res.data || []);
    } catch (err) {
      toast.error("Failed to load industry options");
    }
  };
  // const fetchTimeZone = async () => {
  //   try {
  //     const res = await dropdownApi.fetchAllTimeZone();
  //     const cleanedList = res.data.data.map(item => ({
  //       ...item,
  //       timeZone: item.timeZone.replace(/\s\d{2}:\d{2}:\d{2}$/, "")
  //     }));
  //     setTimeZonOptions(cleanedList || []);
  //   } catch (err) {
  //     toast.error("Failed to load timeZone options");
  //   }
  // };
  const fetchPrepaidTrialPlan = async () => {
    setLoadingTrialPlan(true);
    try {
      const ctRes = await LicenceApis.getCustomerType()
      if (ctRes.status !== 200) throw new Error('Failed to fetch customer types');
      const customerTypes = ctRes.data || [];
      const prepaidTypes = customerTypes.filter((ct) => (ct.billingModel === "prepaid" && ct.isActive === true));
      if (prepaidTypes.length === 0) {
        toast.error('No "Prepaid" customer type found');
        return;
      }
      const planResponses = await Promise.all(
        prepaidTypes.map((ct) => {
          const customerTypeId = ct.id;
          return LicenceApis.EligiblePlans(customerTypeId).then((r) => {
            if (r.status !== 200) throw new Error('Failed to fetch eligible plans');
            return r;
          });
        })
      );
      const allPlans = planResponses
        .flatMap((response) => response.data || [])
        .filter(
          (plan, index, self) =>
            index === self.findIndex((p) => p.id === plan.id)
        );
      const trialPlans = allPlans.filter(
        (plan) => plan.isTrialPlan === true && plan.isActive === true
      );
      if (trialPlans.length === 0) {
        toast.error('No trial-eligible plan found for the Prepaid customer type');
        return;
      }
      setTrialEligiblePlan(trialPlans[0]);
    } catch (err) {
      toast.error('Failed to load trial-eligible plan');
    } finally {
      setLoadingTrialPlan(false);
    }
  };

  const fetchAll = async () => {
    await Promise.allSettled([
      fetchEmployees(),
      fetchIndustries(),
      fetchPrepaidTrialPlan(),
      // fetchTimeZone()
    ])
  };
  // Check payment status if returning from Stripe
  useEffect(() => {
    if (sessionId && success) {
      pollPaymentStatus(sessionId);
    } else if (cancelled) {
      setPaymentStatus('cancelled');
    }
  }, [sessionId, success, cancelled]);
  useEffect(() => {
    fetchAll();
  }, []);
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }
      const data = await response.json();
      if (data.payment_status === 'paid') {
        setPaymentComplete(true);
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }
      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      setPaymentStatus('error');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      // no leading spaces, so a field can never be "only spaces"
      [field]: stripLeadingSpaces(value)
    }));
  };

  // Trim trailing spaces when the user leaves any text field of the form
  const handleFormBlur = (e) => {
    const { id, value, tagName } = e.target;
    if (id in validators) setFieldTouched(prev => ({ ...prev, [id]: true }));
    if ((tagName === 'INPUT' || tagName === 'TEXTAREA') && id in formData && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed !== value) handleChange(id, trimmed);
    }
  };

  // const calculateTotal = () => {
  //   const basePrice = parseInt(selectedPlan.price.replace('$', '')) || 0;
  //   const multiplier = formData.billingCycle === 'annual' ? 10 : 1; // 2 months free on annual
  //   return basePrice * formData.userCount * multiplier;
  // };
  const calculateTotal = () => {
    const selectedPricing = trialEligiblePlan?.pricing?.find(
      (pricing) => pricing.interval === formData.billingCycle
    );
    const pricePerSeat = Number(selectedPricing?.pricePerSeat) || 0;
    return pricePerSeat * Number(formData.userCount || 0);
  };

  const selectedPricing = selectedPlan?.pricing?.find(
    (pricing) => pricing.interval === formData.billingCycle
  );
  const pricePerSeat = Number(selectedPricing?.pricePerSeat) || 0;
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isProcessing) return;
    if (trap.looksLikeBot()) {
      // pretend it worked, send nothing
      setPaymentComplete(true);
      return;
    }
    // the trial plan is needed to build the request; never fall back to the old payment path without it
    if (loadingTrialPlan || !trialEligiblePlan) {
      toast.error('Plan details are still loading. Please wait a moment and try again.');
      return;
    }
    setIsProcessing(true);
    const cleanedData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value
      ])
    );
    // remove a leading 0 / pasted country code before building the number
    cleanedData.phone = cleanedData.phone ? normalizePhoneDigits(cleanedData.phone) : '';
    const phone =
      cleanedData.phone
        ? `${selectedCountry.code}${cleanedData.phone}`
        : "";
    const emailError = validateEmail(cleanedData.email);
    if (emailError) {
      toast.error(emailError);
      setIsProcessing(false);
      return; // stop here
    }
    // 2️⃣ PHONE NEXT
    const phoneError = cleanedData.phone ? validatePhone(cleanedData.phone) : null;
    if (phoneError) {
      toast.error(phoneError);
      setIsProcessing(false);
      return; // stop here
    }
    if (trialEligiblePlan?.isTrialPlan === true) {
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      const payload = {
        companyName: cleanedData.companyName,
        firstName: cleanedData.firstName,
        lastName: cleanedData.lastName,
        email: cleanedData.email,
        phone: phone,
        industry: cleanedData.industry || null,
        timeZone: cleanedData.timeZone || null,
        startDate: formattedDate,
        noOfEmployees: cleanedData.companySize || null
      }
      const createTrialAndSubscription = async () => {
        const licenceRes = await SubcriptionApis.trailCall(payload);
        const licenceData = licenceRes?.data ?? {};
        const subscriptionPayload = {
          customerId: licenceData.companyCode,
          customerTypeId: trialEligiblePlan.eligibleCustomerTypes[0].id,
          planId: trialEligiblePlan.id,
          interval: cleanedData.billingCycle,
          seatCount: Number(cleanedData.userCount) || 0,
          // stripePaymentMethodId: null,
          customerEmail: licenceData.userEmail,
          // customAmountCents:pricePerSeat
        };
        const tokenPayload = {
          aiTokensLimit: 1000000,
          usedAiTokens: 0
        }
        const contactPayload = {
          customerId: licenceData.companyCode,
          email: licenceData.userEmail,
          name: `${cleanedData.firstName} ${cleanedData.lastName}`.trim(),
          phone: phone,
        };
        const [subscriptionRes] = await Promise.all([
          LicenceApis.createSubscriptions(subscriptionPayload),
          LicenceApis.createPrimaryContact(contactPayload),
          SubcriptionApis.addAiTokens(tokenPayload, licenceData.companyCode)
        ]);

        return subscriptionRes;
      };

      const promise = createTrialAndSubscription();
      toast.promise(promise, {
        loading: "Sending trial request...",
        success: "Trial request sent successfully!",
        error: "Failed to send trial request. Try again later."
      });
      try {
        await promise;
        setPaymentComplete(true);
        scrollToTop()
      }
      catch (error) {
        // the promise toast already shows a generic message; add the server's reason when there is one
        const reason = error?.response?.data?.message?.message || (typeof error?.response?.data?.message === 'string' ? error.response.data.message : null);
        if (reason) toast.error(reason);
        setIsProcessing(false)
      } finally {
        setIsProcessing(false);
      }
    } else {
      try {
        const checkoutData = {
          plan_id: selectedPlan.id.toString(),
          user_count: cleanedData.userCount,
          billing_cycle: cleanedData.billingCycle,
          company_name: cleanedData.companyName,
          customer_email: cleanedData.email,
          customer_name: `${cleanedData.firstName} ${cleanedData.lastName}`
        };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(checkoutData)
        });

        if (!response.ok) {
          throw new Error('Failed to create checkout session');
        }

        const data = await response.json();

        // Redirect to Stripe Checkout
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error('No checkout URL received');
        }

      } catch (error) {
        toast.error('Failed to start checkout process. Please try again.');
        setIsProcessing(false);
      }
    }
  };
  const normalizePhoneDigits = (value, country = selectedCountry) => {
    let digits = (value || '').replace(/\D/g, '');
    if (digits.length > country.max) {
      if (digits.startsWith('0')) {
        const stripped = digits.replace(/^0+/, '');
        if (stripped.length >= country.min && stripped.length <= country.max) digits = stripped;
      }
      if (digits.length > country.max && digits.startsWith(country.code)) {
        const rest = digits.slice(country.code.length).replace(/^0+/, '');
        if (rest.length >= country.min && rest.length <= country.max) digits = rest;
      }
    }
    return digits;
  };
  const validatePhone = (value, country = selectedCountry) => {
    const digits = normalizePhoneDigits(value, country);
    if (!digits) return null; // not required
    if (!/^\d+$/.test(digits)) return 'Digits only — no spaces or symbols.';
    if (digits.length < country.min)
      return `Too short — need at least ${country.min} digits.`;
    if (digits.length > country.max)
      return `Too long — max ${country.max} digits.`;
    return null;
  };

  // Keep the phone error in sync no matter which side changes: the number or the country code.
  // Runs after state has updated, so it always sees the latest phone + selectedCountry.
  useEffect(() => {
    if (!touched.phone) return;
    setErrors(prev => {
      const err = validatePhone(formData.phone, selectedCountry);
      return prev.phone === err ? prev : { ...prev, phone: err };
    });
  }, [formData.phone, selectedCountry, touched.phone]);

  // Handle different payment states
  if (paymentStatus === 'processing') {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
            <h1 className="display-md text-white mb-6">Processing Payment...</h1>
            <p className="body-lg mb-8">
              Please wait while we confirm your payment. This should only take a few seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'cancelled') {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-slate-500/10 flex items-center justify-center mx-auto mb-8">
              <ArrowLeft className="h-10 w-10 text-slate-400" />
            </div>
            <h1 className="display-md text-white mb-6">Payment Cancelled</h1>
            <p className="body-lg mb-8">
              Your payment was cancelled. No charges have been made to your account.
            </p>
            <Link to={`/checkout?plan=${planId}`}>
              <Button className="btn-primary">
                Try Again
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'error' || paymentStatus === 'timeout' || paymentStatus === 'expired') {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-8">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="display-md text-white mb-6">Payment Issue</h1>
            <p className="body-lg mb-8">
              {paymentStatus === 'timeout' && "Payment verification timed out. Please check your email for confirmation."}
              {paymentStatus === 'expired' && "Your payment session has expired. Please try again."}
              {paymentStatus === 'error' && "There was an error processing your payment. Please try again."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/checkout?plan=${planId}`}>
                <Button className="btn-primary">
                  Try Again
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Contact Support
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-8">
              <Check className="h-10 w-10 text-blue-500" />
            </div>
            <h1 className="display-md text-white mb-6">Welcome to GrowSquare!</h1>
            <p className="body-lg mb-8">
              Thank you for subscribing to the {trialEligiblePlan?.name} plan. Your account is being set up
              and you'll receive login credentials within the next few minutes.
            </p>
            <div className="bg-slate-800 rounded-lg p-6 mb-8 text-left">
              <h3 className="h3 text-white mb-4">What's Next?</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-blue-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Account Setup</p>
                    <p className="body-sm text-slate-400">We're configuring your GrowSquare instance with your selected features</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-blue-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Welcome Email</p>
                    <p className="body-sm text-slate-400">Login credentials and getting started guide will be sent to {formData.email}</p>
                  </div>
                </div>
                {/* <div className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-blue-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Onboarding Call</p>
                    <p className="body-sm text-slate-400">Our team will schedule a personalized onboarding session</p>
                  </div>
                </div> */}
              </div>
            </div>
            <Link to="/">
              <Button className="btn-secondary">
                Return to Homepage
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  // const selectedEmployee = employeeOptions.find(
  //   (item) => item.nomOfEmployeeId.toString() === selectedEmployeeId
  // );


  // Characters/format/length check + work-email rule (shared validator)
  const validateEmail = (value) =>
    emailError(value, { workOnly: true, personalDomains: PERSONAL_DOMAINS });
  const handleBlur = (field, value) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'phone' && value) {
      const normalized = normalizePhoneDigits(value);
      if (normalized !== value) handleChange('phone', normalized);
    }
    const err = field === 'email' ? validateEmail(value) : validatePhone(value);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  // Button stays disabled until all required fields have real text (spaces only do not count),
  // the email is a valid work email, any phone entered is valid, and the terms are accepted
  const canSubmit =
    Object.keys(validators).every((field) => !validators[field](formData[field])) &&
    !validateEmail((formData.email || '').trim()) &&
    !(formData.phone && validatePhone(formData.phone)) &&
    isAgreed;

  return (
    <div className="min-h-screen section-padding">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>

          <div className="text-center mb-12">
            <h1 className="display-md text-white mb-6">Complete Your Subscription</h1>
            {/* <p className="body-lg max-w-2xl mx-auto">
              You're just one step away from revolutionizing your CRM experience with {trialEligiblePlan?.name} Plan.
            </p> */}
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="feature-card sticky"> {/* top-8 */}
                <CardHeader>
                  <CardTitle className="h3 text-white">Order Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="body-md font-medium text-white">{trialEligiblePlan?.name} Plan</p>
                        <p className="body-sm text-slate-400">
                          {formData.userCount} user{formData.userCount !== 1 ? 's' : ''} × {formData.billingCycle}
                        </p>
                      </div>
                      <Badge className="bg-blue-500/10 text-blue-400">
                        {pricePerSeat}/{formData.billingCycle === 'annual' ? 'year' : 'month'}
                      </Badge>
                    </div>

                    {/* {formData.billingCycle === 'annual' && (
                      <div className="flex items-center justify-between text-green-400">
                        <p className="body-sm">Annual Discount (2 months free)</p>
                        <p className="body-sm">-${(parseInt(pricePerSeat.replace('$', '')) || 0) * formData.userCount * 2}</p>
                      </div>
                    )} */}

                    <div className="border-t border-slate-700 pt-4">
                      <div className="flex items-center justify-between">
                        <p className="h3 text-white">Total</p>
                        <p className="h3 text-blue-400">${calculateTotal()}</p>
                      </div>
                      <p className="body-sm text-slate-400 mt-1">
                        {/* {formData.billingCycle === 'annual' ? 'Billed annually' : 'Billed monthly'} */}
                        {formData.billingCycle === 'annual' ? 'Billed monthly' : 'Billed monthly'}
                      </p>
                    </div>

                    <div className="space-y-3 pt-4 border-t border-slate-700">
                      <p className="body-sm font-medium text-white">Included Features:</p>
                      {selectedPlan.features.slice(0, 5).map((feature, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <Check className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <span className="body-sm text-slate-400">{feature}</span>
                        </div>
                      ))}
                      {selectedPlan.features.length > 5 && (
                        <p className="body-sm text-blue-400">+{selectedPlan.features.length - 5} more features</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Checkout Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} onBlur={handleFormBlur} className="space-y-8">
                {/* Company Information */}
                <Card className="feature-card">
                  <CardHeader>
                    <CardTitle className="h3 text-white">Company Information</CardTitle>
                    <CardDescription className="body-md">
                      Tell us about your company to customize your GrowSquare experience.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyName" className="text-white">Company Name *</Label>
                      <Input
                        id="companyName"
                        autoComplete="organization"
                        value={formData.companyName}
                        onChange={(e) => handleChange('companyName', e.target.value)}
                        required
                        {...fieldA11y('companyName', getError('companyName'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('companyName'))}`}
                      />
                      <FieldError id="companyName-error" message={getError('companyName')} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="companySize" className="text-white">Company Size</Label>
                        <Select onValueChange={(value) => {
                          // setSelectedEmployeeId(value); 
                          handleChange('companySize', value)
                        }}>
                          <SelectTrigger id="companySize" className={FIELD_CLASS}>
                            <SelectValue placeholder="Select size" />
                            {/* <SelectValue>
                              {selectedEmployee ? selectedEmployee.nomOfEmployee : "Select size"}
                            </SelectValue> */}
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_CLASS}>
                            {employeeOptions.map((item, i) => (
                              <SelectItem key={i} value={item.nomOfEmployee}>
                                {item.nomOfEmployee}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="industry" className="text-white">Industry</Label>
                        <Select onValueChange={(value) => handleChange('industry', value)}>
                          <SelectTrigger id="industry" className={FIELD_CLASS}>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_CLASS}>
                            {industryOptions.length > 0 ? (
                              industryOptions.map((item, i) => (
                                <SelectItem key={i} value={item.industryName}>
                                  {item.industryName}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem disabled>Loading...</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* <div className="space-y-2">
                        <Label htmlFor="timeZone" className="text-white">Time Zone</Label>
                        <Select onValueChange={(value) => handleChange('timeZone', value)}>
                          <SelectTrigger id="timeZone" className={FIELD_CLASS}>
                            <SelectValue placeholder="Select timeZone" />
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_CLASS}>
                            {timeZonOptions.length > 0 ? (
                              timeZonOptions.map(item => (
                                <SelectItem key={item.timeZoneId} value={item.timeZone}>
                                  {item.timeZone}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem disabled>Loading...</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div> */}
                    </div>
                  </CardContent>
                </Card>

                {/* Plan Configuration */}
                <Card className="feature-card">
                  <CardHeader>
                    <CardTitle className="h3 text-white">Plan Configuration</CardTitle>
                    <CardDescription className="body-md">
                      Customize your subscription details.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="userCount" className="text-white">Number of Users</Label>
                        <Input id="userCount" disabled={trialEligiblePlan?.isTrialPlan === true}
                          type="number"
                          min="1"
                          max="1000"
                          value={formData.userCount}
                          onChange={(e) => handleChange('userCount', Math.min(1000, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                          className={FIELD_CLASS}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="billingCycle" className="text-white">Billing Cycle</Label>
                        <Select value={formData.billingCycle} onValueChange={(value) => handleChange('billingCycle', value)} disabled={trialEligiblePlan?.isTrialPlan === true}>
                          <SelectTrigger id="billingCycle" className={FIELD_CLASS}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_CLASS}>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="annual">Annual (Save 2 months)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Billing Information */}
                <Card className="feature-card">
                  <CardHeader>
                    <CardTitle className="h3 text-white">Billing Information</CardTitle>
                    <CardDescription className="body-md">
                      Enter your billing details for subscription management.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-white">First Name *</Label>
                        <Input
                          id="firstName"
                          autoComplete="given-name"
                          value={formData.firstName}
                          onChange={(e) => handleChange('firstName', e.target.value)}
                          required
                          {...fieldA11y('firstName', getError('firstName'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('firstName'))}`}
                        />
                        <FieldError id="firstName-error" message={getError('firstName')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-white">Last Name *</Label>
                        <Input
                          id="lastName"
                          autoComplete="family-name"
                          value={formData.lastName}
                          onChange={(e) => handleChange('lastName', e.target.value)}
                          required
                          {...fieldA11y('lastName', getError('lastName'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('lastName'))}`}
                        />
                        <FieldError id="lastName-error" message={getError('lastName')} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-white">Email Address *</Label>
                        <Input
                          id="email"
                          autoComplete="email"
                          type="email"
                          value={formData.email}
                          onBlur={(e) => handleBlur('email', e.target.value)}
                          // onChange={(e) => handleChange('email', e.target.value)}
                          onChange={(e) => {
                            handleChange('email', e.target.value);
                            if (touched.email || e.target.value.trim()) {
                              setErrors(prev => ({ ...prev, email: validateEmail(e.target.value) }));
                            }
                          }}
                          required
                          {...fieldA11y('email', errors.email)}
                          className={`${FIELD_CLASS} ${errorClass(errors.email)}`}
                        />
                        <FieldError id="email-error" message={errors.email} />
                      </div>
                      {/* <div className="space-y-2">
                        <Label htmlFor="phone" className="text-white">Phone Number</Label>
                        <Input
                          id="phone"
                          autoComplete="tel"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          className={FIELD_CLASS}
                        />
                      </div> */}
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-white">Phone Number</Label>
                        <div className={`flex rounded-md border bg-slate-800 overflow-hidden focus-within:ring-1 ${errors.phone
                          ? 'border-red-500 focus-within:ring-red-500'
                          : 'border-slate-700 focus-within:border-blue-500 focus-within:ring-blue-500'
                          }`}>
                          <Select style={{ width: "50%" }}
                            value={selectedCountry.keyId}
                            onValueChange={(value) => {
                              const country = COUNTRY_PHONE_RULES.find(
                                (c) => c.keyId === value
                              );
                              if (!country) return;
                              setSelectedCountry(country);
                              // a number already exists -> start validating it live against the new country
                              if (formData.phone) setTouched(prev => ({ ...prev, phone: true }));
                            }}
                          // className="bg-slate-700 text-white text-sm px-2 border-r border-slate-600 focus:outline-none h-full max-w-[110px]"
                          >
                            <SelectTrigger aria-label="Country code" className="w-[100px] h-full flex items-center justify-between px-2 
             bg-slate-800 text-white data-[placeholder]:text-slate-400
             border-0 outline-none shadow-none
             focus:outline-none focus:ring-0 focus:shadow-none
             focus-visible:outline-none focus-visible:ring-0
             data-[state=open]:outline-none" >
                              <SelectValue placeholder="Select country code" />
                              {/* <SelectValue>
                              {selectedEmployee ? selectedEmployee.nomOfEmployee : "Select size"}
                            </SelectValue> */}
                            </SelectTrigger>
                            <SelectContent className={DROPDOWN_CLASS}>
                              {COUNTRY_PHONE_RULES.map((c, i) => (
                                <SelectItem key={i} value={c.keyId}>
                                  {c.flag} {c.code}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, '');
                              handleChange('phone', digits);
                            }}
                            onBlur={(e) => handleBlur('phone', e.target.value)}
                            autoComplete="tel-national"
                            {...fieldA11y('phone', errors.phone)}
                            className="bg-transparent border-0 text-white shadow-none focus-visible:ring-0 [&:-webkit-autofill]:[-webkit-text-fill-color:#fff] [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#1e293b]"
                          />
                        </div>
                        <FieldError id="phone-error" message={errors.phone} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment Information */}
                {/* <Card className="feature-card">
                  <CardHeader>
                    <CardTitle className="h3 text-white flex items-center">
                      <CreditCard className="mr-2 h-5 w-5" />
                      Payment Information
                    </CardTitle>
                    <CardDescription className="body-md">
                      Your payment information is encrypted and secure.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cardholderName" className="text-white">Cardholder Name *</Label>
                      <Input
                        id="cardholderName"
                        value={formData.cardholderName}
                        onChange={(e) => handleChange('cardholderName', e.target.value)}
                        required
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cardNumber" className="text-white">Card Number *</Label>
                      <Input
                        id="cardNumber"
                        placeholder="1234 5678 9012 3456"
                        value={formData.cardNumber}
                        onChange={(e) => handleChange('cardNumber', e.target.value)}
                        required
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="expiryDate" className="text-white">Expiry Date *</Label>
                        <Input
                          id="expiryDate"
                          placeholder="MM/YY"
                          value={formData.expiryDate}
                          onChange={(e) => handleChange('expiryDate', e.target.value)}
                          required
                          className={FIELD_CLASS}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cvv" className="text-white">CVV *</Label>
                        <Input
                          id="cvv"
                          placeholder="123"
                          value={formData.cvv}
                          onChange={(e) => handleChange('cvv', e.target.value)}
                          required
                          className={FIELD_CLASS}
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 text-slate-400 text-sm">
                      <Shield className="h-4 w-4" />
                      <span>Your payment information is secured with 256-bit SSL encryption</span>
                    </div>
                  </CardContent>
                </Card> */}

                <BotTrap trap={trap} />
                {/* Submit Button */}
                <div className="flex flex-col pt-6 space-y-4">
                  {/* Terms and Conditions checkbox */}
                  <label className="flex items-start space-x-2 text-sm text-slate-300">
                    <input
                      id="terms"
                      type="checkbox"
                      required
                      checked={isAgreed}
                      onChange={(e) => setIsAgreed(e.target.checked)}
                      className="mt-1 accent-blue-600 cursor-pointer"
                    />
                    <span>
                      I have read and agree to the{" "}
                      <a
                        href="/termsAndCondition"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline"
                      >
                        GrowSquare Terms & Conditions
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacyPolicy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline"
                      >
                        Privacy Policy
                      </a>.
                    </span>
                  </label> </div>
                <div className="flex items-center justify-between pt-6">
                  <div className="flex items-center space-x-2 text-slate-400">
                    {/* <Lock className="h-4 w-4" />
                    <span className="body-sm">Secure checkout powered by Stripe</span> */}
                  </div>
                  <Button
                    type="submit"
                    className="btn-primary glow-effect"
                    disabled={isProcessing || !canSubmit || loadingTrialPlan || !trialEligiblePlan}
                  >
                    {/* {isProcessing ? 'Processing...' : `Subscribe for $${calculateTotal()}`} */}
                    {isProcessing
                      ? 'Processing...'
                      : loadingTrialPlan
                        ? 'Loading plan...'
                        : "Start Free Trial"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CheckoutPage;